import { CampaignStep } from '@forwardflow/types/demo-conversation'
import { create } from 'zustand'

interface CampaignPhase {
  order: number
  campaign: string
  steps: CampaignStep[]
}

interface CampaignData {
  type: string
  data: CampaignPhase[]
}

interface NextStepResult {
  nextCampaign: string
  nextStep: number
  nextAction: string | null
}

interface ActiveConversationState {
  isActive: boolean
  lastCampaignType: string
  lastCampaignPhase: string
  lastStep: number
  startIndex: number
}

interface DemoCampaignStore {
  campaigns: Record<string, CampaignData>
  activeConversations: Record<number, ActiveConversationState>
  
  setCampaignData: (campaignType: string, data: CampaignData) => void
  getNextStep: (campaignType: string, currentCampaign: string, currentStep: number) => NextStepResult
  getCampaignSteps: (campaignType: string, campaignName: string) => CampaignStep[]
  
  startActiveConversation: (leadId: number, campaignType: string, campaignPhase: string, step: number, timelineIndex: number) => void
  endActiveConversation: (leadId: number) => void
  isActiveConversation: (leadId: number) => boolean
  getActiveConversationInfo: (leadId: number) => ActiveConversationState | null
  getActiveConversationStartIndex: (leadId: number) => number | null
}

export const useDemoCampaignStore = create<DemoCampaignStore>((set, get) => ({
  campaigns: {},
  activeConversations: {},

  setCampaignData: (campaignType: string, data: CampaignData) => {
    set((state) => ({
      campaigns: {
        ...state.campaigns,
        [campaignType]: data
      }
    }))
  },

  getNextStep: (campaignType: string, currentCampaign: string, currentStep: number): NextStepResult => {
    const state = get()
    const campaignData = state.campaigns[campaignType]

    if (!campaignData) {
      return {
        nextCampaign: currentCampaign,
        nextStep: currentStep + 1,
        nextAction: null
      }
    }

    const currentPhase = campaignData.data.find(
      phase => phase.campaign === currentCampaign.toLowerCase()
    )

    if (!currentPhase) {
      return {
        nextCampaign: currentCampaign,
        nextStep: currentStep + 1,
        nextAction: null
      }
    }

    const nextStepInPhase = currentPhase.steps.find(
      step => step.order === currentStep + 1
    )

    if (nextStepInPhase) {
      return {
        nextCampaign: currentCampaign,
        nextStep: nextStepInPhase.order,
        nextAction: nextStepInPhase.actionType
      }
    }

    const nextPhase = campaignData.data.find(
      phase => phase.order === currentPhase.order + 1
    )

    if (nextPhase && nextPhase.steps.length > 0) {
      return {
        nextCampaign: nextPhase.campaign,
        nextStep: nextPhase.steps[0].order,
        nextAction: nextPhase.steps[0].actionType
      }
    }

    return {
      nextCampaign: currentCampaign,
      nextStep: currentStep,
      nextAction: null
    }
  },

  getCampaignSteps: (campaignType: string, campaignName: string): CampaignStep[] => {
    const state = get()
    const campaignData = state.campaigns[campaignType.toLowerCase()]

    if (!campaignData) {
      console.warn('No campaign data available')
      return []
    }

    const campaign = campaignData.data.find((c: any) => c.campaign === campaignName.toLowerCase())

    if (!campaign) {
      console.warn(`No campaign found with name: ${campaignName}`)
      return []
    }

    return campaign.steps || []
  },

  startActiveConversation: (leadId: number, campaignType: string, campaignPhase: string, step: number, timelineIndex: number) => {
    set((state) => ({
      activeConversations: {
        ...state.activeConversations,
        [leadId]: {
          isActive: true,
          lastCampaignType: campaignType,
          lastCampaignPhase: campaignPhase,
          lastStep: step,
          startIndex: timelineIndex
        }
      }
    }))
  },

  endActiveConversation: (leadId: number) => {
    set((state) => ({
      activeConversations: {
        ...state.activeConversations,
        [leadId]: {
          ...state.activeConversations[leadId],
          isActive: false
        }
      }
    }))
  },

  isActiveConversation: (leadId: number) => {
    const state = get()
    return state.activeConversations[leadId]?.isActive || false
  },

  getActiveConversationInfo: (leadId: number) => {
    const state = get()
    return state.activeConversations[leadId] || null
  },

  getActiveConversationStartIndex: (leadId: number) => {
    const state = get()
    return state.activeConversations[leadId]?.startIndex || null
  }
}))